import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { message, Spin, Table, type TableProps, Tooltip } from 'antd';
import axios from 'axios';
import { capitalize, isEmpty } from 'lodash';
import { useContext, useState } from 'react';
import type { Books_ConnectionQuery } from '../../__generated__/graphql';
import { AppContext } from '../../AppContext';
import { LIMIT, ROUTES, TOKEN } from '../../common/constants';
import {
  formatCurrency,
  formatNumber,
  notEmpty,
  truncateToChars,
} from '../../common/utils';
import ActionBtn from '../../components/ActionBtn';
import PageBreadcrumb from '../../components/PageBreadcrumb';
import useRouter from '../../hooks/useRouter';
import {
  AppActionType,
  type AppContextType,
} from '../../types/appContext.type';
import { GET_BOOKS } from './graphql/queries';

type BooksData = NonNullable<
  NonNullable<Books_ConnectionQuery['books_connection']>['nodes']
>[number];

const Book = () => {
  const { navigate } = useRouter();
  const { dispatch } = useContext(AppContext) as AppContextType;
  const [variables, setVariables] = useState({
    pagination: {
      page: 1,
      pageSize: LIMIT,
    },
  });
  const [exportingBookId, setExportingBookId] = useState<string | null>(null);

  const { data, loading } = useQuery(GET_BOOKS, {
    variables,
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handleExportCSV = async (bookId: string, bookName: string) => {
    setExportingBookId(bookId);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_REST_URL}/api/book/${bookId}/export-book`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(bookName || '').replace(/\s+/g, '_')}-export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('CSV exported successfully');
    } catch (error) {
      message.error('Failed to export CSV, Please try again!');
      console.error('Error downloading CSV:', error);
    } finally {
      setExportingBookId(null);
    }
  };

  const columns: TableProps<BooksData>['columns'] = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      // width: 600,
      fixed: 'left',
      render: (value) => (
        <Tooltip placement="top" title={value}>
          {truncateToChars(value, 100)}
        </Tooltip>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (value) => (
        <Tooltip placement="top" title={value}>
          {truncateToChars(value, 50)}
        </Tooltip>
      ),
    },
    {
      title: 'Book Url',
      width: 150,
      dataIndex: 'url',
      key: 'url',
      render: (value) =>
        isEmpty(value) ? (
          '-'
        ) : (
          <Tooltip placement="top" title={value}>
            <a href={value ?? ''} target="_blank" rel="noopener noreferrer">
              {truncateToChars(value, 50)}
            </a>
          </Tooltip>
        ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (value) => (
        <p>
          <Tooltip
            placement="top"
            title={
              Array.isArray(value) && value.length > 0
                ? value.map((item) => capitalize(item.toLowerCase())).join(', ')
                : '-'
            }
          >
            {Array.isArray(value) && value.length > 0
              ? truncateToChars(
                  value
                    .map((item) => capitalize(item.toLowerCase()))
                    .join(', '),
                  50,
                )
              : '-'}
          </Tooltip>
        </p>
      ),
    },
    {
      title: 'Token Counts',
      dataIndex: 'tokenCounts',
      key: 'tokenCounts',
      width: 100,
      render: (value) => formatNumber(value ?? 0),
    },
    {
      title: 'Total Price',
      dataIndex: 'priceCounts',
      key: 'priceCounts',
      width: 100,
      render: (value) => formatCurrency({ value: value ?? 0 }),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (value) => (
        <Tooltip placement="top" title={value}>
          {truncateToChars(value, 50)}
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'documentId',
      key: 'documentId',
      fixed: 'right',
      width: 80,
      align: 'center',
      render: (value, record) => {
        const isExporting = exportingBookId === value;
        return (
          <Tooltip title="Export CSV">
            <ActionBtn
              icon={isExporting ? <Spin size="small" /> : <DownloadOutlined />}
              type="text"
              disabled={isExporting}
              onClick={(e) => {
                e.stopPropagation();
                handleExportCSV(value, record?.title ?? '');
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const onChange: TableProps<BooksData>['onChange'] = (pagination) => {
    setVariables((prev) => ({
      ...prev,
      pagination: {
        ...prev?.pagination,
        page: pagination?.current ?? 1,
        pageSize: pagination?.pageSize ?? LIMIT,
      },
    }));
  };

  return (
    <>
      <PageBreadcrumb items={[{ title: 'Book' }]} />
      <div className="book-container">
        <Table
          dataSource={data?.books_connection?.nodes?.filter(notEmpty)}
          columns={columns}
          pagination={{
            total: data?.books_connection?.pageInfo?.total ?? 0,
            current: variables?.pagination?.page ?? 1,
            pageSize: variables?.pagination?.pageSize,
          }}
          onChange={onChange}
          scroll={{
            x: 'max-content',
            y: 'calc(100vh - 272px)',
          }}
          loading={loading}
          onRow={(record) => {
            return {
              onClick: () => {
                dispatch({
                  data: record?.title ?? '',
                  type: AppActionType.setBookName,
                });
                navigate(`${record?.documentId}${ROUTES.SECTION}`);
              },
            };
          }}
        />
      </div>
    </>
  );
};

export default Book;
